/**
 * 3D Room Visualizer — Styles of window, door and garage door
 *
 * Each kind of opening comes in a few styles: a pattern (how it is built — six
 * raised panels, a carriage door, a grid of glass) in a set of colours. The
 * first style of each kind is the default, and what an opening with no style
 * chosen is drawn as.
 *
 * Plain data, shared by the mesh builder (which draws them) and the designer's
 * toolbar (which lists them; the names shown are in designer/strings.ts, keyed
 * by id). Styles are looks only: nothing here is a product, and none of it is
 * ever costed.
 */

export type OpeningKindId = 'window' | 'door' | 'garageDoor';

/**
 * hung — two sashes, the lower sliding; picture — one clear pane; grid —
 * colonial grilles; prairie — a bar in from each side; fractional — a row of
 * small panes along the top; diamond — a lattice; traditional-hung — the upper
 * sash gridded; prairie-hung / grid-hung — both sashes patterned.
 */
export type WindowPattern = 'hung' | 'picture' | 'grid' | 'prairie' | 'fractional' | 'diamond' | 'traditional-hung' | 'prairie-hung' | 'grid-hung';
/**
 * six-panel — two columns of three raised panels; shaker — two flat fields;
 * half-glass — glass above the lock rail; flush — a plain slab;
 * craftsman — three lites over two panels; craftsman-six — six lites over
 * three panels; slab-lites — a slab with five frosted lites stacked;
 * three-quarter — a 2 × 3 grid of glass over one panel; prairie — a 3 × 3 grid.
 */
export type DoorPattern = 'six-panel' | 'shaker' | 'half-glass' | 'flush' | 'craftsman' | 'craftsman-six' | 'slab-lites' | 'three-quarter' | 'prairie';
/**
 * raised — sections of raised panels; carriage — planked leaves with glass
 * along the top; full-view — a grid of glass; planks — modern flush planks
 * with a stack of slim windows down one side; x-brace — carriage leaves with a
 * cross of boards and divided lites; barn — the same over vertical planks.
 */
export type GarageDoorPattern = 'raised' | 'carriage' | 'full-view' | 'planks' | 'x-brace' | 'barn';

/** Colours of one style, as hex numbers. Not every pattern uses every one. */
export interface OpeningPalette {
  /** The frame / jamb set in the wall. */
  frame: number;
  /** A window's sill. */
  sill: number;
  /** The door itself: slab, or the garage door's body. */
  body: number;
  /** Raised panels, or the boards laid over a carriage / shaker door. */
  panel: number;
  /** Joints, grooves and the bars of a glass door. */
  line: number;
  /** Handles, levers, hinges. */
  hardware: number;
  /** The door is wood: its body and boards carry a grain. */
  wood?: boolean;
  /** The style's id, for the export's tags (the render's per-item texture placements). */
  id?: string;
}

export interface OpeningStyle {
  id: string;
  kind: OpeningKindId;
  pattern: WindowPattern | DoorPattern | GarageDoorPattern;
  palette: OpeningPalette;
}

const WHITE_TRIM = 0xf4f4f1;
/** A window in black: its frame, sash and grilles, and its sill. */
const BLACK: Partial<OpeningPalette> = { frame: 0x2b2d30, sill: 0x2b2d30 };

const style = (kind: OpeningKindId, id: string, pattern: OpeningStyle['pattern'], palette: Partial<OpeningPalette>): OpeningStyle => ({
  id,
  kind,
  pattern,
  palette: { frame: WHITE_TRIM, sill: 0xe9e9e4, body: 0xe9e5dc, panel: 0xdcd7cc, line: 0xb4b8b8, hardware: 0x8d8d8d, ...palette, id },
});

export const OPENING_STYLES: Record<OpeningKindId, OpeningStyle[]> = {
  window: [
    style('window', 'window-hung-white', 'hung', {}),
    style('window', 'window-hung-black', 'hung', BLACK),
    style('window', 'window-picture-white', 'picture', {}),
    style('window', 'window-picture-black', 'picture', BLACK),
    style('window', 'window-picture-bronze', 'picture', { frame: 0x4d3d31, sill: 0x44362b }),
    style('window', 'window-grid-white', 'grid', {}),
    style('window', 'window-grid-black', 'grid', BLACK),
    style('window', 'window-prairie-white', 'prairie', {}),
    style('window', 'window-prairie-black', 'prairie', BLACK),
    style('window', 'window-fractional-white', 'fractional', {}),
    style('window', 'window-fractional-black', 'fractional', BLACK),
    style('window', 'window-diamond-white', 'diamond', {}),
    style('window', 'window-diamond-black', 'diamond', BLACK),
    style('window', 'window-traditional-hung-white', 'traditional-hung', {}),
    style('window', 'window-traditional-hung-black', 'traditional-hung', BLACK),
    style('window', 'window-prairie-hung-white', 'prairie-hung', {}),
    style('window', 'window-prairie-hung-black', 'prairie-hung', BLACK),
    style('window', 'window-grid-hung-white', 'grid-hung', {}),
    style('window', 'window-grid-hung-black', 'grid-hung', BLACK),
  ],
  door: [
    style('door', 'door-six-panel-white', 'six-panel', {}),
    style('door', 'door-shaker-walnut', 'shaker', { body: 0x7a5540, panel: 0x7a5540, hardware: 0x1c1c1c, wood: true }),
    style('door', 'door-half-glass-black', 'half-glass', { body: 0x232528, panel: 0x2c2f33, hardware: 0xb9b9b9 }),
    style('door', 'door-flush-slate', 'flush', { body: 0x4b5560, panel: 0x4b5560, hardware: 0xcfcfcf }),
    // Craftsman: lites across the top over panels — stained wood, and painted red with six lites.
    style('door', 'door-craftsman-oak', 'craftsman', { body: 0x9a6b42, panel: 0x9a6b42, line: 0x5e3f25, hardware: 0x1c1c1c, wood: true }),
    style('door', 'door-craftsman-six-red', 'craftsman-six', { body: 0xb3362b, panel: 0xb3362b, line: 0x7d231b, hardware: 0x1c1c1c }),
    // A modern slab with five frosted lites stacked down it.
    style('door', 'door-slab-lites-black', 'slab-lites', { body: 0x1e2022, panel: 0x1e2022, line: 0x2c2f33, hardware: 0xb9b9b9 }),
    // Three-quarter glass in a grid over one panel — black, oak; and a nine-lite prairie grid in walnut.
    style('door', 'door-three-quarter-black', 'three-quarter', { body: 0x232528, panel: 0x2c2f33, line: 0x232528, hardware: 0xb9b9b9 }),
    style('door', 'door-three-quarter-oak', 'three-quarter', { body: 0xc48f5a, panel: 0xc48f5a, line: 0x8a5d33, hardware: 0x1c1c1c, wood: true }),
    style('door', 'door-prairie-walnut', 'prairie', { body: 0x7a5540, panel: 0x7a5540, line: 0x4a3221, hardware: 0x1c1c1c, wood: true }),
  ],
  garageDoor: [
    style('garageDoor', 'garage-raised-white', 'raised', { body: 0xdfe2e2, panel: 0xeceeee, line: 0xb4b8b8 }),
    style('garageDoor', 'garage-raised-charcoal', 'raised', { body: 0x3a3e43, panel: 0x464b51, line: 0x26292c, hardware: 0x151515 }),
    style('garageDoor', 'garage-carriage-walnut', 'carriage', { body: 0x7d573b, panel: 0x8a6244, line: 0x4a3221, hardware: 0x151515, wood: true }),
    style('garageDoor', 'garage-full-view-black', 'full-view', { body: 0x1f2124, panel: 0x1f2124, line: 0x1f2124, hardware: 0x1f2124 }),
    // Modern flush cedar planks, black trim, a stack of windows down one side.
    style('garageDoor', 'garage-planks-cedar', 'planks', { frame: 0x1f1f1f, body: 0xb5713a, panel: 0xb5713a, line: 0x6d4120, hardware: 0x1f1f1f, wood: true }),
    // Carriage doors with cross braces and divided-lite windows along the top.
    style('garageDoor', 'garage-x-brace-white', 'x-brace', { body: 0xf1f1ee, panel: 0xf7f7f4, line: 0xcfcfca, hardware: 0x151515 }),
    style('garageDoor', 'garage-x-brace-grey', 'x-brace', { body: 0x8b887f, panel: 0x97948b, line: 0x6a675f, hardware: 0x151515, wood: true }),
    // Vertical cedar planks under the cross braces.
    style('garageDoor', 'garage-barn-cedar', 'barn', { body: 0xb87a3e, panel: 0xc08445, line: 0x7a4b22, hardware: 0x151515, wood: true }),
  ],
};

/** The style an opening is drawn in: the one named, or its kind's default. */
export function openingStyle(kind: OpeningKindId, id?: string): OpeningStyle {
  const styles = OPENING_STYLES[kind];
  return styles.find((s) => s.id === id) ?? styles[0];
}
